import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trash2, UserPlus, BookOpen, X, Key, Copy, Check } from 'lucide-react';
import { adminApi } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';

export default function AdminUsersPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [showEnrollments, setShowEnrollments] = useState<string | null>(null);
  const [resetModal, setResetModal] = useState<{ userId: string; userName: string; tempPassword?: string } | null>(null);
  const [resetting, setResetting] = useState(false);
  const [copied, setCopied] = useState(false);

  const { data: users, isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminApi.getUsers(),
  });

  const { data: courses } = useQuery({
    queryKey: ['admin-courses'],
    queryFn: () => adminApi.getCourses(),
  });

  const { data: enrollments } = useQuery({
    queryKey: ['admin-enrollments'],
    queryFn: () => adminApi.getEnrollments(),
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) => adminApi.updateUserRole(id, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast({ title: 'Success', description: 'User role updated successfully!' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-enrollments'] });
      toast({ title: 'Success', description: 'User deleted successfully!' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const createEnrollmentMutation = useMutation({
    mutationFn: ({ userId, courseId }: { userId: string; courseId: string }) =>
      adminApi.createEnrollment(userId, courseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-enrollments'] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setShowEnrollModal(false);
      setSelectedUserId('');
      setSelectedCourseId('');
      toast({ title: 'Success', description: 'User enrolled successfully!' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteEnrollmentMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteEnrollment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-enrollments'] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast({ title: 'Success', description: 'Enrollment removed successfully!' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleRoleChange = (userId: string, currentRole: string) => {
    const newRole = currentRole === 'admin' ? 'student' : 'admin';
    if (confirm(`Change user role from ${currentRole} to ${newRole}?`)) {
      updateRoleMutation.mutate({ id: userId, role: newRole });
    }
  };

  const handleDelete = (userId: string, userName: string) => {
    if (confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      deleteUserMutation.mutate(userId);
    }
  };

  const handleEnroll = () => {
    if (!selectedUserId || !selectedCourseId) {
      toast({ title: 'Error', description: 'Please select a user and a course', variant: 'destructive' });
      return;
    }
    createEnrollmentMutation.mutate({ userId: selectedUserId, courseId: selectedCourseId });
  };

  const handleRemoveEnrollment = (enrollmentId: string, userName: string, courseName: string) => {
    if (confirm(`Remove enrollment for "${userName}" from "${courseName}"?`)) {
      deleteEnrollmentMutation.mutate(enrollmentId);
    }
  };

  const handleResetPassword = async (userId: string, userName: string) => {
    setResetting(true);
    setCopied(false);
    setResetModal({ userId, userName });
    try {
      const res = await fetch(`/api/admin/users/${userId}/reset-password`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        setResetModal({ userId, userName, tempPassword: data.tempPassword });
        toast({ title: 'Success', description: 'Password reset successfully!' });
      } else {
        toast({ title: 'Error', description: data.error || 'Failed to reset password', variant: 'destructive' });
        setResetModal(null);
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to reset password', variant: 'destructive' });
      setResetModal(null);
    } finally {
      setResetting(false);
    }
  };

  const handleCopyPassword = () => {
    if (resetModal?.tempPassword) {
      navigator.clipboard.writeText(resetModal.tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getUserEnrollments = (userId: string) => {
    const data = enrollments as any;
    const list = data?.data || data || [];
    return list.filter((e: any) => e.user?.id === userId || e.userId === userId);
  };

  const coursesList = (courses as any)?.data || courses || [];

  if (isLoading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">Users</h1>
        <Button onClick={() => setShowEnrollModal(true)} className="bg-[#8B0000] hover:bg-[#6B0000] text-white">
          <UserPlus className="h-4 w-4 mr-2" />
          Enroll User in Course
        </Button>
      </div>

      {/* Enroll Modal */}
      {showEnrollModal && (
        <Card className="mb-6 border-2 border-[#8B0000]/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Enroll User in Course</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowEnrollModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium mb-1">Select User</label>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full border rounded-md p-2 bg-background"
                >
                  <option value="">-- Choose a user --</option>
                  {users && users.map((user: any) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Select Course</label>
                <select
                  value={selectedCourseId}
                  onChange={(e) => setSelectedCourseId(e.target.value)}
                  className="w-full border rounded-md p-2 bg-background"
                >
                  <option value="">-- Choose a course --</option>
                  {coursesList.map((course: any) => (
                    <option key={course.id} value={course.id}>
                      {course.titleEn || course.title}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                onClick={handleEnroll}
                disabled={createEnrollmentMutation.isPending}
                className="bg-[#8B0000] hover:bg-[#6B0000] text-white"
              >
                {createEnrollmentMutation.isPending ? 'Enrolling...' : 'Enroll'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-4">Name</th>
                <th className="text-left p-4">Email</th>
                <th className="text-left p-4">Phone</th>
                <th className="text-left p-4">Role</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4">Joined</th>
                <th className="text-left p-4">Enrollments</th>
                <th className="text-left p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users && users.length > 0 ? users.map((user: any) => {
                const userEnrollments = getUserEnrollments(user.id);
                return (
                  <tr key={user.id} className="border-t align-top">
                    <td className="p-4 font-medium">{user.name}</td>
                    <td className="p-4">{user.email}</td>
                    <td className="p-4 text-sm text-muted-foreground">{user.phone || "—"}</td>
                    <td className="p-4">
                      <button
                        onClick={() => handleRoleChange(user.id, user.role)}
                        className={`px-2 py-1 rounded text-xs hover:opacity-80 transition ${
                          user.role === 'admin'
                            ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                      >
                        {user.role}
                      </button>
                    </td>
                    <td className="p-4">
                      <button
                        onClick={async () => {
                          if (!confirm(`${user.emailVerified ? 'Deactivate' : 'Activate'} user "${user.name}"?`)) return;
                          try {
                            const res = await fetch(`/api/admin/users/${user.id}/verify`, { method: 'PUT', credentials: 'include' });
                            const data = await res.json();
                            if (data.success) {
                              queryClient.invalidateQueries({ queryKey: ['admin-users'] });
                              toast({ title: 'Success', description: `User ${data.emailVerified ? 'activated' : 'deactivated'}` });
                            }
                          } catch (e) { toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' }); }
                        }}
                        className={`px-2 py-1 rounded text-xs font-medium transition ${
                          user.emailVerified
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        }`}
                        title={user.emailVerified ? 'Click to deactivate' : 'Click to activate'}
                      >
                        {user.emailVerified ? 'Active' : 'Pending'}
                      </button>
                    </td>
                    <td className="p-4 text-muted-foreground">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      {userEnrollments.length > 0 ? (
                        <div>
                          <button
                            onClick={() => setShowEnrollments(showEnrollments === user.id ? null : user.id)}
                            className="text-sm text-[#8B0000] hover:underline flex items-center gap-1"
                          >
                            <BookOpen className="h-3 w-3" />
                            {userEnrollments.length} course{userEnrollments.length > 1 ? 's' : ''}
                          </button>
                          {showEnrollments === user.id && (
                            <div className="mt-2 space-y-1">
                              {userEnrollments.map((enrollment: any) => (
                                <div key={enrollment.id} className="flex items-center justify-between bg-muted/50 rounded px-2 py-1 text-sm">
                                  <span className="mr-2">{enrollment.course?.titleEn || 'Unknown Course'}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground">{Math.round(enrollment.progressPercent || 0)}%</span>
                                    <button
                                      onClick={() => handleRemoveEnrollment(enrollment.id, user.name, enrollment.course?.titleEn || 'course')}
                                      className="text-red-500 hover:text-red-700"
                                      title="Remove enrollment"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">None</span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleResetPassword(user.id, user.name)}
                        className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                        title="Reset Password"
                      >
                        <Key className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(user.id, user.name)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      </div>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-muted-foreground">
                    No users yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Reset Password Modal */}
      {resetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Reset Password</h2>
                <Button variant="ghost" size="sm" onClick={() => setResetModal(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              {resetting ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Resetting password for {resetModal.userName}...</p>
                </div>
              ) : resetModal.tempPassword ? (
                <div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Password has been reset for <strong>{resetModal.userName}</strong>. Share this temporary password with the user. They will be required to change it on their next login.
                  </p>
                  <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg mb-4">
                    <code className="flex-1 text-lg font-mono font-bold text-orange-800 select-all">
                      {resetModal.tempPassword}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCopyPassword}
                      className="text-orange-600 hover:text-orange-700"
                    >
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4 text-sm">
                    <span className="text-amber-600 mt-0.5">⚠️</span>
                    <p className="text-amber-800">
                      This password is shown only once. The user must change it upon next login. All existing sessions have been invalidated.
                    </p>
                  </div>
                  <Button className="w-full" onClick={() => setResetModal(null)}>
                    Done
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
